import React, { useMemo, useState } from 'react'; // Import useMemo and useState
import { Link } from 'react-router-dom';
import { useCart } from '../context/cartContext';
import { CartItem, displayPrice, OrderItem, Customer } from '../utils/types';
import {
    createOrder,
    sendWhatsAppMessage, // Import WhatsApp function
    sendOrderEmail     // Import Email function
} from '../utils/api'; import { useNavigate } from 'react-router-dom';
// import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

// --- Import Context Hooks and Auth Utils ---
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from '../utils/auth'; // Assuming path is correct



// Access API URL for constructing the PDF link
const API_URL = window.APP_CONFIG?.API_URL || window.APP_CONFIG?.REACT_APP_API_URL;

// --- Cart Component ---
const Cart: React.FC = () => {
    const { state, dispatch } = useCart();
    const { items } = state;
    const navigate = useNavigate();
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [newOrderId, setNewOrderId] = useState<number | null>(null);

    // --- State for Fulfillment Options ---
    const [fulfillmentType, setFulfillmentType] = useState<'delivery' | 'pickup'>('delivery');
    const [pickupTime, setPickupTime] = useState('');
    const [pickerName, setPickerName] = useState('');
    const [pickerPhone, setPickerPhone] = useState('');
    const [pickerCarPlate, setPickerCarPlate] = useState('');
    // --- End Fulfillment State ---

    // --- Get User and Customer Context ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer, customers: customerList } = useCustomerContext(); // Get full customer list too

    // --- Determine Effective User (for checkout) ---
    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff', 'technician']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            // Admin is acting as another customer
            const customerRole = selectedSessionCustomer?.role || 'dealer'; // Default or fetched role
            return {
                id: selectedSessionCustomer.user_id, // The user ID associated with the customer
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id, // The actual customer ID
                // Attempt to get name from selectedSessionCustomer or fallback
                name: selectedSessionCustomer?.name || 'Unknown Customer'
            };
        } else {
            // Use the logged-in user's details
            let loggedInUserRole: string | undefined;
            let customerIdForUser: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                // Attempt to get customer ID from localStorage (might be just the ID)
                const customerInfo = localStorage.getItem('customer');
                if (customerInfo) {
                    // Check if it's just the ID or a full object (adjust parsing if needed)
                    try {
                        const parsedCustomer = JSON.parse(customerInfo);
                        customerIdForUser = typeof parsedCustomer === 'number' ? parsedCustomer : parsedCustomer?.id;
                    } catch {
                        // Handle case where 'customer' in localStorage might not be valid JSON
                        console.warn("Could not parse 'customer' from localStorage in Cart.");
                    }
                }
                // Fallback if not found in localStorage but might be nested in user object
                if (customerIdForUser === undefined) {
                    customerIdForUser = loggedInUser?.customer?.id;
                }

            } catch (e) {
                console.error("Failed to parse rolesPermissions/customer from localStorage in Cart:", e);
            }

            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: customerIdForUser, // Use the determined customer ID
                name: loggedInUser?.name || 'Guest'
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);
    // --- End Determine Effective User ---


    // const handleRemoveFromCart = (cartItemId: string) => {
    const handleRemoveFromCart = (itemToRemove: CartItem) => { // Pass the full item

        dispatch({
            type: 'REMOVE_FROM_CART',
            // payload: { cartItemId },
            payload: itemToRemove, // Pass the CartItem object

        });
    };


    const handleUpdateItemQuantity = (itemToUpdate: CartItem, newQuantity: number) => {
        const updatedQuantity = Math.max(1, newQuantity); // Ensure quantity is at least 1
        // console.log('clicked!');
        // Only dispatch if the quantity actually changes
        // if (itemToUpdate.quantity === updatedQuantity) return;
        if (itemToUpdate.quantity === updatedQuantity) {
            // console.log('Quantity not changed, skipping dispatch. Current:', itemToUpdate.quantity, 'Target:', updatedQuantity); // For debugging
            return;
        }

        const payload = { ...itemToUpdate, quantity: updatedQuantity };
        // console.log('Dispatching UPDATE_ITEM_QUANTITY with payload:', payload); // For debugging
        dispatch({ type: 'UPDATE_ITEM_QUANTITY', payload });
    };

    const calculateTotal = () => {
        return items.reduce((total: number, item: CartItem) => total + item.price * item.quantity, 0);
    };

    const handleCheckout = async () => {
        setIsCheckingOut(true);

        // --- Use effectiveUser details ---
        const effectiveCustomerId = effectiveUser.customer_id;
        const effectiveUserId = effectiveUser.id;

        // Find the customer's name from the full list if acting as, otherwise use effectiveUser.name
        let customerNameForOrder = effectiveUser.name;
        if (hasRole(['super_admin', 'staff', 'technician']) && selectedSessionCustomer) {
            const foundCustomer = customerList.find(c => c.id === effectiveCustomerId);
            customerNameForOrder = foundCustomer?.name || selectedSessionCustomer.name || 'Unknown Customer';
        }


        if (effectiveCustomerId === undefined || effectiveCustomerId === null || effectiveUserId === undefined) {
            toast.error("Cannot checkout: Customer or User information is missing.");
            console.error("Checkout failed: effectiveCustomerId or effectiveUserId is missing.", effectiveUser);
            return;
        }
        // --- End Use effectiveUser details ---


        const orderItems: OrderItem[] = items.map((item: CartItem): OrderItem => {
            // Modifier price calculation seems okay, but ensure item.price is the correct base
            // const modifierTotalPrice = item.modifiers.reduce((sum, modifier) => sum + Number(modifier.product_value), 0);
            // const totalPrice = Number(item.price) + Number(modifierTotalPrice); // This seems incorrect, item.price should already include modifier price if added correctly


            return {
                order_id: 0, // Will be set by backend
                customer_id: effectiveCustomerId, // Use effective customer ID
                category_id: item.product_category, // Use category from cart item
                product_category: item.product_category,
                product_id: item.id,
                product_code: item.product_code,
                product_name: item.product_name,
                quantity: item.quantity,
                product_value: item.product_value, // SRP for reference
                price: item.price, // This should be the final price (base + modifiers) calculated when added to cart
                total_price: (item.price * item.quantity),
                payment_desc: item.product_code,
                payment_for: item.product_name,
                qty: item.quantity,
                unit_price: item.price, // Unit price is the final price per item
                balance: (item.price * item.quantity),
                tranx_details: '', // Or add relevant details
                modifiers: item.modifiers,
                checkout: false, // Assuming this is correct for new order items
            };
        });

        const orderPayload = {
            customerId: effectiveCustomerId, // Use effective customer ID
            orderType: 'order',
            tableId: '1', // Consider if this needs to be dynamic
            items: orderItems,
            status: 'pending',
            amountToPay: calculateTotal(),
            userId: effectiveUserId, // Use effective user ID (could be admin's or customer's user_id)
            table: [], // Assuming empty is correct
            customer_name: customerNameForOrder, // Use the determined customer name
        };

        // --- Add Fulfillment Details to Payload ---
        if (fulfillmentType === 'pickup') {
            (orderPayload as any).fulfillment = {
                fulfillment_type: 'pickup',
                pickup_time_slot: pickupTime,
                picker_name: pickerName,
                picker_phone: pickerPhone,
                picker_car_plate: pickerCarPlate,
            };
        }
        // --- End Fulfillment Details ---


        const settings = JSON.parse(localStorage.getItem('settings') || '{}');

        let shopOwnerTelephone = settings?.phone;
        

        try {
            // No need to get token here, api instance should handle it
            // const token = localStorage.getItem('token');

            const response = await createOrder(orderPayload);
            console.log('Order created:', response);

            dispatch({ type: 'CLEAR_CART' });

            toast.success('Order created successfully!', {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });

            // Navigate to the order details page using the ID from the response
            if (response.data?.id) {

                const newOrderId = response.data.id;
                setNewOrderId(newOrderId);
                const newOrderUuid = response.data.order_uuid; // Assume this is returned

                // --- Determine Customer Contact Details for Notifications ---
                let targetPhoneNumber: string | undefined | null;
                let recipientEmail: string | undefined | null;
                let customerForNotifications: Customer | undefined | null = null;

                if (hasRole(['super_admin', 'staff', 'technician']) && selectedSessionCustomer) {
                    customerForNotifications = selectedSessionCustomer;
                } else if (effectiveUser.customer_id) {
                    customerForNotifications = customerList.find(c => c.id === effectiveUser.customer_id);
                }

                targetPhoneNumber = customerForNotifications?.phone;
                if (targetPhoneNumber && targetPhoneNumber.startsWith('0')) {
                    targetPhoneNumber = '6' + targetPhoneNumber;
                }
                recipientEmail = customerForNotifications?.email;

                // --- Send WhatsApp Message ---
                if (targetPhoneNumber && newOrderUuid) {
                    try {
                        const pdfLink = `${API_URL}/orders/${newOrderUuid}/pdf`;

                        const whatsappMessage = `*${settings?.shortname || 'Our Store'}* sent you a document.\n\n\nOrder *${newOrderId}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n\n*Note: This is a bot message, please do not reply.*\n\n*Please contact ${settings?.shortname || 'Us'} at ${settings.phone || 'our contact number'} instead.*`;

                        const whatsappMessage2owner = `*${settings?.shortname || 'Our Store'}* : New Order *${newOrderId}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n*Note: This is a bot new order ${newOrderId} notification.`;



                        await sendWhatsAppMessage(targetPhoneNumber, whatsappMessage);
                        await sendWhatsAppMessage(shopOwnerTelephone, whatsappMessage2owner);
                        await sendWhatsAppMessage('601112163808', whatsappMessage2owner);

                        toast.info(`Order PDF link sent via WhatsApp to ${targetPhoneNumber}.`);

                    } catch (whatsappError: any) {

                        await sendWhatsAppMessage(shopOwnerTelephone, 'Failed to send WhatsApp message after order creation:' + whatsappError);
                        await sendWhatsAppMessage('601112163808', 'Failed to send WhatsApp message after order creation:' + whatsappError);

                        console.error('Failed to send WhatsApp message after order creation:', whatsappError);

                        toast.error(`Failed to send WhatsApp link: ${whatsappError.message || 'Unknown error'}`);
                    }

                } else if (newOrderUuid && !targetPhoneNumber) {
                    toast.warn("Order created, but customer phone number is missing for WhatsApp notification.");


                    await sendWhatsAppMessage(shopOwnerTelephone, 'Order created, but customer phone number is missing for WhatsApp notification.');
                    await sendWhatsAppMessage('601112163808', 'Order created, but customer phone number is missing for WhatsApp notification.');

                }

                // --- Send Email ---
                if (recipientEmail) {
                    try {
                        await sendOrderEmail(newOrderId, recipientEmail);
                        toast.success(`Order confirmation email sent to ${recipientEmail}.`);
                    } catch (emailError: any) {
                        console.error('Failed to send order email after order creation:', emailError);
                        toast.error(`Failed to send order email: ${emailError.message || 'Unknown error'}`);
                    }
                } else {
                    toast.warn("Order created, but customer email is missing for email notification.");
                }

                navigate(`/order/${newOrderId}`);

            } else {
                console.error("Order created, but no ID received in response.", response);
                navigate('/order-history'); // Fallback navigation
            }

        } catch (error) {
            setIsCheckingOut(false);

            console.error('Error creating order:', error);
            toast.error('Error creating order! Please try again.');
        }
        // No finally block to reset isCheckingOut, as we navigate away on success.
        // On error, we want the user to be able to see their cart and try again.
        // If there's an error, we can reset the state to allow another attempt.

        await sendWhatsAppMessage(shopOwnerTelephone, 'Order attempted, but need DEBUG? ask Boyd.');
        await sendWhatsAppMessage('601112163808', 'Order attempted, but need DEBUG? ask Boyd.');

    };

    // console.log('Effective User in Cart:', effectiveUser);
    // console.log('Cart Items:', items);

    if (isCheckingOut) {
        return <div className="container mx-auto p-4 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"> {/* eslint-disable-line @typescript-eslint/no-unused-vars */}
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-800">
                    {newOrderId ? `Order #${newOrderId} Placed Successfully!` : 'You have successfully placed an order!'}
                </h1>
                <p className="text-gray-600">Please check back on the order page to track its progress.</p>
                <p className="text-gray-500">Processing and redirecting...</p>
            </div>
        </div>;
    }

    return (
        <div className="container mx-auto p-4 pb-36">
            <h1 className="text-2xl font-bold mb-4">Cart</h1>
            {items.length === 0 ? (
                <p className="text-gray-600">Your cart is empty</p>
            ) : (
                <ol className="list-decimal list-inside divide-y divide-gray-200" style={{ counterReset: 'my-counter' }}>
                    {items.map((item: CartItem) => ( // Removed index as key part
                        // Use item.cartItemId which should be unique
                        <li key={item.cartItemId} className="py-4 flex items-center justify-between before:content-[counter(my-counter)] before:mr-4" style={{ counterIncrement: 'my-counter' }}>
                            <div className="flex-grow">
                                <p className="font-medium">
                                    <Link to={`${window.APP_CONFIG?.ORIGIN_URL || window.APP_CONFIG?.PUBLIC_URL || ''}/shop/product/${item.id}/${item.product_name.replace(/\s+/g, '-').toLowerCase()}`}>

                                        {item.product_name}
                                    </Link>
                                    <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {item.product_code}</span>
                                    &nbsp;<span className='price-div'>({displayPrice(Number(item.price))})</span> {/* Display final item price */}
                                </p>

                                {item.modifiers && item.modifiers.length > 0 && ( // Check if modifiers exist
                                    <div className='modifiers-div'>
                                        <ul className="list-disc list-inside ">
                                            {item.modifiers.map((modifier) => (
                                                // Use modifier.id for uniqueness within the item
                                                <li key={`${item.cartItemId}-mod-${modifier.id}`} className="text-xs">
                                                    {modifier.product_name}
                                                    <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {modifier.product_code}</span>
                                                    &nbsp;<span className='price-div'>({displayPrice(Number(modifier.product_value))})  </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <p className="price-div text-sm text-gray-500">
                                    {/* Display calculation based on final item price */}
                                    {displayPrice(item.price)} x {item.quantity} = {displayPrice(item.price * item.quantity)}
                                </p>
                            </div>
                            <div className="ml-4 flex items-center space-x-2">
                                {/* Quantity Adjuster */}
                                <div className="flex items-center">
                                    <button
                                        onClick={() => handleUpdateItemQuantity(item, item.quantity - 1)}
                                        disabled={item.quantity <= 1}
                                        className="px-3 py-1 border border-gray-300 rounded-l-md hover:bg-gray-100 focus:outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => {
                                            const newQuantity = parseInt(e.target.value, 10);
                                            if (!isNaN(newQuantity)) {
                                                handleUpdateItemQuantity(item, newQuantity);
                                            }
                                        }}
                                        className="w-12 text-center border-t border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm py-1"
                                        min="1"
                                    />
                                    <button
                                        onClick={() => handleUpdateItemQuantity(item, item.quantity + 1)}
                                        className="px-3 py-1 border border-gray-300 rounded-r-md hover:bg-gray-100 focus:outline-none text-sm"
                                    >
                                        +
                                    </button>
                                </div>

                                <button
                                    // onClick={() => handleRemoveFromCart(item.cartItemId)}
                                    onClick={() => handleRemoveFromCart(item)}

                                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                                >
                                    Remove
                                </button>
                            </div>
                        </li>
                    ))}
                </ol>
            )}
            {items.length > 0 && (
                <div className="mt-4">
                    <p className="price-div text-lg font-semibold">
                        Subtotal: {displayPrice(calculateTotal())}
                    </p>

                    {/* Fulfillment Options */}
                    <div className="my-6 p-4 border rounded-lg bg-gray-50">
                        <h3 className="text-lg font-semibold mb-3">Fulfillment Method</h3>
                        <div className="flex space-x-4 mb-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="fulfillment"
                                    value="delivery"
                                    checked={fulfillmentType === 'delivery'}
                                    onChange={() => setFulfillmentType('delivery')}
                                    className="form-radio h-4 w-4 text-blue-600"
                                />
                                <span className="font-medium">Delivery</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="fulfillment"
                                    value="pickup"
                                    checked={fulfillmentType === 'pickup'}
                                    onChange={() => setFulfillmentType('pickup')}
                                    className="form-radio h-4 w-4 text-blue-600"
                                />
                                <span className="font-medium">Self Pickup</span>
                            </label>
                        </div>

                        {/* Conditional Pickup Details Form */}
                        {fulfillmentType === 'pickup' && (
                            <div className="p-4 border-t border-gray-200 space-y-3 animate-fade-in">
                                <h4 className="font-semibold text-md text-gray-700">Pickup Details</h4>
                                <div>
                                    <label htmlFor="pickupDate" className="block text-sm font-medium text-gray-600">Estimated Pickup Date & Time</label>
                                    <div className="flex space-x-2 mt-1">
                                        <input
                                            type="date"
                                            id="pickupDate"
                                            value={pickupTime.split('T')[0] || ''}
                                            onChange={e => {
                                                const currentTime = pickupTime.split('T')[1] || '08:00';
                                                setPickupTime(`${e.target.value}T${currentTime}`);
                                            }}
                                            className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                        />
                                        <select
                                            id="pickupTimeSlot"
                                            value={pickupTime.split('T')[1] || '08:00'}
                                            onChange={e => {
                                                const currentDate = pickupTime.split('T')[0] || new Date().toISOString().split('T')[0];
                                                setPickupTime(`${currentDate}T${e.target.value}`);
                                            }}
                                            className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                        >
                                            {Array.from({ length: (18 - 8) * 4 + 1 }, (_, i) => {
                                                const totalMinutes = 8 * 60 + i * 15;
                                                const hour = Math.floor(totalMinutes / 60);
                                                const minute = totalMinutes % 60;
                                                if (hour > 18 || (hour === 18 && minute > 0)) return null;
                                                const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                                return <option key={time} value={time}>{time}</option>;
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="pickerName" className="block text-sm font-medium text-gray-600">Picker's Name</label>
                                    <input type="text" id="pickerName" placeholder="e.g., John Doe" value={pickerName} onChange={e => setPickerName(e.target.value)} className="mt-1 block w-full md:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                                <div>
                                    <label htmlFor="pickerPhone" className="block text-sm font-medium text-gray-600">Picker's Phone</label>
                                    <input type="tel" id="pickerPhone" placeholder="e.g., 0123456789" value={pickerPhone} onChange={e => setPickerPhone(e.target.value)} className="mt-1 block w-full md:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                                <div>
                                    <label htmlFor="pickerCarPlate" className="block text-sm font-medium text-gray-600">Car Plate Number</label>
                                    <input type="text" id="pickerCarPlate" placeholder="e.g., WXX 1234" value={pickerCarPlate} onChange={e => setPickerCarPlate(e.target.value)} className="mt-1 block w-full md:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                            </div>
                        )}

                        {fulfillmentType === 'delivery' && (
                            <div className="p-4 border-t border-gray-200">
                                <p className="text-sm text-gray-600">Your order will be delivered to your registered address.</p>
                            </div>
                        )}
                    </div>

                    <p className="price-div text-lg font-semibold">
                        Total: {displayPrice(calculateTotal())}
                    </p>
                    <button
                        onClick={handleCheckout}
                        // Disable button if customer/user info is missing
                        disabled={effectiveUser.customer_id === undefined || effectiveUser.id === undefined}
                        className={`mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ${(effectiveUser.customer_id === undefined || effectiveUser.id === undefined) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Checkout
                    </button>
                    {/* Show warning if button is disabled */}
                    {(effectiveUser.customer_id === undefined || effectiveUser.id === undefined) && (
                        <p className="text-red-500 text-sm mt-2">Cannot checkout: Missing customer or user information.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Cart;
